import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAgency } from '@/features/agencies/hooks/useAgency'

export interface SubscriptionLimits {
  residences: number // -1 = illimité
  users: number
  units: number // Renamed from 'lots' to match database
}

export interface SubscriptionUsage {
  residences: number
  users: number
  units: number // Renamed from 'lots' to match database
}

export interface SubscriptionStatus {
  subscription_status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete'
  tier: 'essential' | 'professional' | 'integral'
  period_end: string | null
  cancel_at_period_end: boolean
}

export interface SubscriptionCapabilities {
  create_residence: boolean
  add_user: boolean
  create_unit: boolean // NEW: capability to create units
}

export interface SubscriptionInfo {
  limits: SubscriptionLimits
  usage: SubscriptionUsage
  status: SubscriptionStatus
  can: SubscriptionCapabilities
}

export function useSubscription() {
  const { data: agency } = useAgency()

  const { data: subscriptionInfo, isLoading, refetch } = useQuery<SubscriptionInfo>({
    queryKey: ['subscription', agency?.id],
    queryFn: async () => {
      if (!agency?.id) throw new Error('No agency')

      // Use optimized RPC function instead of manual COUNT queries
      // This function reads from subscription_usage table (updated via triggers)
      const { data, error } = await supabase.rpc('get_subscription_limits', {
        p_agency_id: agency.id,
      })

      if (error) {
        console.error('Error fetching subscription limits:', error)
        throw error
      }

      if (!data) {
        throw new Error('No subscription data returned')
      }

      // The RPC function returns everything we need in the correct format
      return data as unknown as SubscriptionInfo
    },
    enabled: !!agency?.id,
    refetchInterval: 30000, // Refresh toutes les 30s
  })

  // Vérifier si une feature est disponible
  const hasFeature = (featureKey: string): boolean => {
    if (!agency?.enabled_features) return false
    const features = agency.enabled_features as string[] | { [key: string]: boolean }

    // Si c'est un array
    if (Array.isArray(features)) {
      return features.includes(featureKey)
    }

    // Si c'est un objet
    return features[featureKey] === true
  }

  // Vérifier si on peut créer une résidence
  const canCreateResidence = (): boolean => {
    return subscriptionInfo?.can.create_residence ?? false
  }

  // Vérifier si on peut ajouter un utilisateur
  const canAddUser = (): boolean => {
    return subscriptionInfo?.can.add_user ?? false
  }

  // Vérifier si on peut créer un lot (unit)
  const canCreateUnit = (): boolean => {
    return subscriptionInfo?.can.create_unit ?? false
  }

  // Calculer le pourcentage d'utilisation
  const getUsagePercentage = (resource: 'residences' | 'users' | 'units'): number => {
    if (!subscriptionInfo) return 0
    const limit = subscriptionInfo.limits[resource]
    const usage = subscriptionInfo.usage[resource]

    if (limit === -1) return 0 // Illimité
    if (limit === 0) return 100

    return Math.round((usage / limit) * 100)
  }

  // Vérifier si proche de la limite (>80%)
  const isNearLimit = (resource: 'residences' | 'users' | 'units'): boolean => {
    return getUsagePercentage(resource) >= 80
  }

  // Vérifier si la limite est atteinte
  const isAtLimit = (resource: 'residences' | 'users' | 'units'): boolean => {
    if (!subscriptionInfo) return false
    const limit = subscriptionInfo.limits[resource]
    const usage = subscriptionInfo.usage[resource]

    if (limit === -1) return false // Illimité
    return usage >= limit
  }

  // Statuts
  const isActive = subscriptionInfo?.status.subscription_status === 'active'
  const isTrialing = subscriptionInfo?.status.subscription_status === 'trialing'
  const isPastDue = subscriptionInfo?.status.subscription_status === 'past_due'
  const isCanceled = subscriptionInfo?.status.subscription_status === 'canceled'
  const isOperational = isActive || isTrialing

  // Nom du tier en français
  const getTierName = () => {
    if (!subscriptionInfo?.status.tier) return ''
    switch (subscriptionInfo.status.tier) {
      case 'essential':
        return 'Essentiel'
      case 'professional':
        return 'Professionnel'
      case 'integral':
        return 'Intégral'
      default:
        return subscriptionInfo.status.tier
    }
  }

  return {
    subscriptionInfo,
    isLoading,
    refetch,

    // Limits & Usage
    limits: subscriptionInfo?.limits,
    usage: subscriptionInfo?.usage,
    status: subscriptionInfo?.status,

    // Capabilities
    canCreateResidence,
    canAddUser,
    canCreateUnit,
    hasFeature,

    // Usage metrics
    getUsagePercentage,
    isNearLimit,
    isAtLimit,

    // Status flags
    isActive,
    isTrialing,
    isPastDue,
    isCanceled,
    isOperational,

    // Tier info
    tier: subscriptionInfo?.status.tier,
    tierName: getTierName(),
  }
}
